import { useEffect, useRef, useState } from "react";

interface IProps {
  restoreCustomer?: (() => Promise<unknown>) | null;
  carts: {
    id?: string | null;
    getCart: (variables: { id: string }) => Promise<{ cart?: unknown } | undefined>;
    createCart: () => Promise<unknown>;
  }[];
}

/**
 * persist 는 토큰과 카트 id 만 들고 있으므로(stores/persist.ts), 부팅 직후 한 번 서버에서
 * 나머지를 채운다.
 *
 * 반환하는 isHydrated 는 "복구 시도가 끝났다"는 뜻이다. 카트의 buyerIdentity 와 customer.id 를
 * 비교하는 쪽은 반드시 이 플래그로 막아야 한다 — 둘 중 하나만 먼저 도착한 중간 상태를 보면
 * 멀쩡한 세션을 불일치로 오판해 카트를 새로 만들어버린다.
 */
export default function useSessionRehydrate({ restoreCustomer, carts }: IProps): { isHydrated: boolean } {
  const [isHydrated, setIsHydrated] = useState(false);
  // StrictMode 이중 실행과 재렌더로 두 번 돌지 않게 막는다
  const isStartedRef = useRef(false);

  useEffect(() => {
    if (isStartedRef.current) return;
    isStartedRef.current = true;

    const customerRestored = restoreCustomer ? restoreCustomer() : null;

    // 카트는 10일 미사용 시 만료되므로 조회 결과가 비면 새로 만든다
    const restoreCarts = carts.map(({ id, getCart, createCart }) =>
      id ? getCart({ id }).then((data) => (data?.cart ? data : createCart())) : null,
    );

    // 한쪽이 실패해도 나머지 복구와 화면 진행을 막지 않는다 (에러 토스트는 각 쿼리가 띄운다)
    void Promise.allSettled([customerRestored, ...restoreCarts]).then(() => {
      setIsHydrated(true);
    });
  }, []);

  return { isHydrated };
}
